import {
  CONTENT_ID_LABEL,
  CONTENT_ID_STATUSES,
  type ContentIdStatus,
  type CreateTrackInput,
  type MusicSource,
} from '@qlk/shared';
import { useId, useState, type FormEvent } from 'react';

import { Nut } from '../../../ui/index.js';

export interface FormBaiNhacProps {
  nguon: MusicSource[];
  dangGui: boolean;
  loi?: string | null;
  onGui: (input: CreateTrackInput) => void;
  onHuy: () => void;
}

const O_NHAP =
  'vung-bam w-full rounded-[10px] border border-vien bg-be-mat px-3 text-co-ban text-chu placeholder:text-chu-mo';

/** Form thêm bài nhạc. Chỉ các trường cần thiết, phần còn lại sửa ở trang chi tiết. */
export function FormBaiNhac({ nguon, dangGui, loi, onGui, onHuy }: FormBaiNhacProps) {
  const id = useId();
  const [sourceId, setSourceId] = useState(nguon[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [genre, setGenre] = useState('');
  const [moods, setMoods] = useState('');
  const [bpm, setBpm] = useState('');
  const [doDai, setDoDai] = useState('');
  const [contentIdStatus, setContentIdStatus] = useState<ContentIdStatus>('not_registered');

  function gui(e: FormEvent) {
    e.preventDefault();
    const phut = doDai.includes(':')
      ? doDai.split(':').reduce((t, p) => t * 60 + Number(p || 0), 0)
      : Number(doDai || 0);

    onGui({
      sourceId,
      title: title.trim(),
      artist: artist.trim(),
      genre: genre.trim() || null,
      moods: moods
        .split(',')
        .map((m) => m.trim())
        .filter(Boolean),
      bpm: bpm ? Number(bpm) : null,
      durationSec: Number.isFinite(phut) ? Math.trunc(phut) : 0,
      contentIdStatus,
      status: 'active',
    });
  }

  return (
    <form onSubmit={gui} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <label htmlFor={`${id}-title`} className="mb-1 block text-phu font-medium text-chu-phu">
          Tên bài <span aria-hidden="true">*</span>
        </label>
        <input
          id={`${id}-title`}
          required
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          className={O_NHAP}
          placeholder="Velvet Dusk"
        />
      </div>

      <div>
        <label htmlFor={`${id}-artist`} className="mb-1 block text-phu font-medium text-chu-phu">
          Nghệ sĩ <span aria-hidden="true">*</span>
        </label>
        <input
          id={`${id}-artist`}
          required
          value={artist}
          onChange={(e) => {
            setArtist(e.target.value);
          }}
          className={O_NHAP}
        />
      </div>

      <div>
        <label htmlFor={`${id}-nguon`} className="mb-1 block text-phu font-medium text-chu-phu">
          Nguồn
        </label>
        <select
          id={`${id}-nguon`}
          value={sourceId}
          onChange={(e) => {
            setSourceId(e.target.value);
          }}
          className={O_NHAP}
        >
          {nguon.map((n) => (
            <option key={n.id} value={n.id}>
              {n.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={`${id}-genre`} className="mb-1 block text-phu font-medium text-chu-phu">
          Thể loại
        </label>
        <input
          id={`${id}-genre`}
          value={genre}
          onChange={(e) => {
            setGenre(e.target.value);
          }}
          className={O_NHAP}
          placeholder="Ambient"
        />
      </div>

      <div>
        <label htmlFor={`${id}-moods`} className="mb-1 block text-phu font-medium text-chu-phu">
          Tâm trạng (cách nhau bằng dấu phẩy)
        </label>
        <input
          id={`${id}-moods`}
          value={moods}
          onChange={(e) => {
            setMoods(e.target.value);
          }}
          className={O_NHAP}
          placeholder="thư giãn, tối"
        />
      </div>

      <div>
        <label htmlFor={`${id}-bpm`} className="mb-1 block text-phu font-medium text-chu-phu">
          BPM
        </label>
        <input
          id={`${id}-bpm`}
          type="number"
          min={20}
          max={300}
          value={bpm}
          onChange={(e) => {
            setBpm(e.target.value);
          }}
          className={O_NHAP}
        />
      </div>

      <div>
        <label htmlFor={`${id}-dai`} className="mb-1 block text-phu font-medium text-chu-phu">
          Độ dài (mm:ss hoặc số giây)
        </label>
        <input
          id={`${id}-dai`}
          value={doDai}
          onChange={(e) => {
            setDoDai(e.target.value);
          }}
          className={O_NHAP}
          placeholder="4:12"
        />
      </div>

      <div className="sm:col-span-2">
        <label htmlFor={`${id}-contentid`} className="mb-1 block text-phu font-medium text-chu-phu">
          Content ID
        </label>
        <select
          id={`${id}-contentid`}
          value={contentIdStatus}
          onChange={(e) => {
            setContentIdStatus(e.target.value as ContentIdStatus);
          }}
          className={O_NHAP}
        >
          {CONTENT_ID_STATUSES.map((s) => (
            <option key={s} value={s}>
              {CONTENT_ID_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      {loi && (
        <p role="alert" className="text-co-ban text-bad-chu sm:col-span-2">
          {loi}
        </p>
      )}

      <div className="flex justify-end gap-3 sm:col-span-2">
        <Nut kieu="phu" onClick={onHuy}>
          Huỷ
        </Nut>
        <Nut kieu="chinh" type="submit" dangChay={dangGui}>
          Thêm bài nhạc
        </Nut>
      </div>
    </form>
  );
}
